import { cn } from "@faire/react/tools/cn";
import { tsv } from "@faire/slate/lib/tsv";
import React from "react";

import { FormHelper, FormLabel } from "./formTexts";
import type { FormGroupLabelVariant } from "./types";

type IProps = React.PropsWithChildren<{
  /**
   * The label of the fieldset.
   */
  label?: React.ReactNode;

  /**
   * The font weight of the fieldset label.
   *
   * @default "regular"
   */
  labelVariant?: FormGroupLabelVariant;

  /**
   * The helper text of the fieldset.
   */
  helper?: React.ReactNode;

  /**
   * The id of the helper.
   */
  helperId?: string;

  /**
   * The data component name. Used for adoption tracking.
   */
  "data-component-name"?: string;
}> &
  React.ComponentProps<typeof FormLabel<"legend">> &
  React.ComponentProps<typeof FormHelper>;

export const Fieldset: React.FC<IProps> = ({
  children,
  className,
  disabled,
  error,
  gap,
  helper,
  helperId,
  label,
  labelVariant = "regular",
  optional,
  ...props
}) => {
  return (
    <fieldset
      className={cn("flex w-full flex-col", className)}
      aria-describedby={helperId}
      aria-invalid={!!error}
      disabled={disabled}
      {...props}
    >
      {label ? (
        <FormLabel
          as="legend"
          className={groupLabelStyle.cls({ labelVariant })}
          disabled={disabled}
          error={error}
          gap={gap}
          optional={optional}
        >
          {label}
        </FormLabel>
      ) : null}
      {children}
      <FormHelper disabled={disabled} error={error} gap={gap} id={helperId}>
        {helper}
      </FormHelper>
    </fieldset>
  );
};

const groupLabelStyle = tsv({
  variants: {
    labelVariant: {
      regular: "fs-form-field__label--paragraph",
      medium: "fs-form-field__label--paragraph-medium",
    },
  },
  defaultVariants: {
    labelVariant: "regular",
  },
});
